import { useState, useEffect, useCallback } from "react";

// Simple client-side state management with API calls

export interface User {
	id: string;
	username: string;
	avatarUrl: string;
	bio: string;
	reputation: number;
	roastCount: number;
	commentCount: number;
	rewriteCount: number;
	upvotesReceived: number;
	createdAt: string;
}

export interface FeedbackItem {
	score: number;
	feedback: string;
	suggestion: string;
}

export interface RoastFeedback {
	headline: FeedbackItem;
	cta: FeedbackItem;
	clarity: FeedbackItem;
	positioning: FeedbackItem;
	trustSignals: FeedbackItem;
	overallScore: number;
	summary: string;
}

export interface Roast {
	id: string;
	userId: string;
	user?: User;
	url: string | null;
	copy: string;
	feedback: RoastFeedback;
	upvotes: number;
	commentCount: number;
	rewriteCount: number;
	createdAt: string;
}

export interface Comment {
	id: string;
	roastId: string;
	userId: string;
	user?: User;
	content: string;
	isRewrite: boolean;
	parentId: string | null;
	upvotes: number;
	createdAt: string;
}

export interface Rewrite {
	id: string;
	roastId: string;
	userId: string;
	user?: User;
	parentRewriteId: string | null;
	originalCopy: string;
	rewrittenCopy: string;
	explanation: string;
	upvotes: number;
	createdAt: string;
}

export interface RoastDetail extends Roast {
	comments: Comment[];
	rewrites: Rewrite[];
}

const api = {
	async get<T>(url: string): Promise<T> {
		const res = await fetch(url);
		return res.json();
	},
	async post<T>(url: string, data: unknown): Promise<T> {
		const res = await fetch(url, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(data),
		});
		return res.json();
	},
};

export function useCurrentUser() {
	const [user, setUser] = useState<User | null>(() => {
		const saved = localStorage.getItem("roast_user");
		return saved ? JSON.parse(saved) : null;
	});

	const login = useCallback(async (username: string, bio?: string) => {
		const u = await api.post<User>("/api/users/create", { username, bio });
		setUser(u);
		localStorage.setItem("roast_user", JSON.stringify(u));
		return u;
	}, []);

	const logout = useCallback(() => {
		setUser(null);
		localStorage.removeItem("roast_user");
	}, []);

	return { user, login, logout };
}

export function useRoasts() {
	const [roasts, setRoasts] = useState<Roast[]>([]);
	const [loading, setLoading] = useState(true);

	const fetchRoasts = useCallback(async () => {
		setLoading(true);
		const data = await api.get<Roast[]>("/api/roasts");
		setRoasts(data);
		setLoading(false);
	}, []);

	useEffect(() => {
		fetchRoasts();
	}, [fetchRoasts]);

	return { roasts, loading, refetch: fetchRoasts };
}

export function useRoastDetail(id: string | null) {
	const [roast, setRoast] = useState<RoastDetail | null>(null);
	const [loading, setLoading] = useState(false);

	const fetchRoast = useCallback(async () => {
		if (!id) return;
		setLoading(true);
		const data = await api.get<RoastDetail>(`/api/roasts/${id}`);
		setRoast(data);
		setLoading(false);
	}, [id]);

	useEffect(() => {
		fetchRoast();
	}, [fetchRoast]);

	return { roast, loading, refetch: fetchRoast };
}

export function useLeaderboard() {
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		api.get<User[]>("/api/leaderboard").then((data) => {
			setUsers(data);
			setLoading(false);
		});
	}, []);

	return { users, loading };
}

export function useUserProfile(id: string | null) {
	const [profile, setProfile] = useState<User | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!id) return;
		setLoading(true);
		api.get<User>(`/api/users/${id}`).then((data) => {
			setProfile(data);
			setLoading(false);
		});
	}, [id]);

	return { profile, loading };
}

export async function createRoast(userId: string, url: string | null, copy: string): Promise<Roast> {
	return api.post<Roast>("/api/roasts/create", { userId, url, copy });
}

export async function addComment(roastId: string, userId: string, content: string, parentId?: string): Promise<Comment> {
	return api.post<Comment>(`/api/roasts/${roastId}/comments`, { userId, content, parentId });
}

export async function addRewrite(
	roastId: string,
	userId: string,
	rewrittenCopy: string,
	explanation: string,
	originalCopy: string,
	parentRewriteId?: string
): Promise<Rewrite> {
	return api.post<Rewrite>(`/api/roasts/${roastId}/rewrites`, {
		userId,
		rewrittenCopy,
		explanation,
		originalCopy,
		parentRewriteId,
	});
}

export async function toggleVote(userId: string, targetId: string, targetType: "roast" | "comment" | "rewrite"): Promise<boolean> {
	const res = await api.post<{ voted: boolean }>("/api/vote", { userId, targetId, targetType });
	return res.voted;
}
